import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TableRow,
  Typography,
} from "@material-ui/core";
import ReplayIcon from "@material-ui/icons/Replay";
import Divider from "@material-ui/core/Divider";
import moment from "moment";
import CurrencyFormat from "react-currency-format";
import TuneIcon from "@material-ui/icons/Tune";
import SearchIcon from "@material-ui/core/SvgIcon/SvgIcon";

import { PageTitle } from "../../../../components/page-title/PageTitle";
import useStyles from "./ActivitiesTab.style";
import { DefaultButton } from "../../components/Buttons";
import Paginator, { DataTypeEnum } from "../../../../models/Paginator";
import ICIPAdminService from "../../../../services/ICIPAdminService";
import { CustomTooltip } from "../../components/Tooltip";
import DynamicPaginationActions from "../../components/DynamicPaginationActions";
import TableData, {
  SortableCell,
  StyledTableCell,
} from "../../components/TableData";
import ActivitySchema from "../../../../models/ActivitySchema";
import AvatarHeader from "../../components/AvatarHeader";
import { MemberStatus } from "../../../../settings/MemberStatus";
import { InputField } from "../../../../components/input-field/InputField";
import BusinessAccount from "../../../../models/BusinessAccount";
import ExpandedContent from "../../components/ExpandedContent";
import BalanceDetails from "../../components/BalanceDetails";

enum LoadingStatus {
  loading,
  failure,
  succeed,
  initial,
  adding,
  paginating,
}

interface ActivityTabData {
  original: Paginator;
  filtered: Paginator;
  wallets: Array<BusinessAccount>;
}

interface ActivitiesTabProps {}

export const ActivitiesTab = (props: ActivitiesTabProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [data, setData] = useState((): ActivityTabData => null);
  const [loadingStatus, setLoadingStatus] = useState(
    (): LoadingStatus => LoadingStatus.initial
  );
  const [query, setQuery] = useState("");

  const errorWrapper = (error?: string): any => {
    return (
      <Box component="div" className={classes.errorWrapper}>
        <Typography component="span">
          {error ? (
            error
          ) : (
            <FormattedMessage id="sharedScreen.unableToLoadData" />
          )}
        </Typography>
        <Box component="div" mt={1}>
          <DefaultButton component="div" mt={1}>
            <FormattedMessage id="sharedScreen.retry" />
          </DefaultButton>
        </Box>
      </Box>
    );
  };

  const renderActivitiesTableHeader = () => {
    return (
      <TableRow>
        <StyledTableCell align="left">#.</StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.designation" />
        </StyledTableCell>
        <StyledTableCell align="left">{""}</StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.description" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.amount" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <SortableCell ontSort={(sortBy) => {}} text="sharedScreen.addedAt" />
        </StyledTableCell>
      </TableRow>
    );
  };

  const renderActivitiesTableRows = () => {
    return data.filtered.items
      .filter((item) => {
        let activity: ActivitySchema = item as ActivitySchema;

        if (query === "") {
          return activity;
        } else if (
          activity.tag.toLowerCase().includes(query.toLowerCase()) ||
          activity.description.toLowerCase().includes(query.toLowerCase()) ||
          (activity.thumbnail !== null &&
            activity.thumbnail.toLowerCase().includes(query.toLowerCase()))
        ) {
          return activity;
        }
      })
      .map((item, index) => {
        let activity = item as ActivitySchema;

        return (
          <TableRow key={index}>
            <StyledTableCell scope="row" component="td" align="left">
              {activity.id}
            </StyledTableCell>
            <StyledTableCell scope="row" component="td" align="left">
              <Typography
                component="span"
                style={{ fontWeight: 600, fontSize: "0.86rem" }}
              >
                {activity.tag}
              </Typography>
            </StyledTableCell>
            <StyledTableCell scope="row" component="td" align="left">
              {activity.thumbnail_url !== null ? (
                <AvatarHeader
                  size={30}
                  avatar={activity.thumbnail_url}
                  validationStatus={MemberStatus.approved}
                  accountStatus={MemberStatus.approved}
                  subTitle=""
                  title={activity.thumbnail !== null ? activity.thumbnail : ""}
                />
              ) : (
                <AvatarHeader
                  size={30}
                  avatar={process.env.PUBLIC_URL + "/user_avatar.png"}
                  validationStatus={MemberStatus.approved}
                  accountStatus={MemberStatus.approved}
                  subTitle=""
                  title={activity.thumbnail !== null ? activity.thumbnail : ""}
                />
              )}
            </StyledTableCell>
            <StyledTableCell scope="row" component="td" align="left">
              <Typography
                component="span"
                style={{ fontWeight: 500, fontSize: "0.88rem" }}
              >
                {activity.description}
              </Typography>
            </StyledTableCell>
            <StyledTableCell scope="row" component="td" align="left">
              <Typography
                component="span"
                style={{
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  color: `${activity.computeSignColor()}`,
                }}
              >
                <CurrencyFormat
                  value={activity.computeAmount()}
                  suffix={` ${
                    activity.currency !== null
                      ? activity.currency.toUpperCase()
                      : ""
                  }`}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                  renderText={(formattedValue: string) => (
                    <Box component="span">{formattedValue}</Box>
                  )}
                />
              </Typography>
            </StyledTableCell>
            <StyledTableCell scope="row" component="td" align="left">
              <Typography
                component="span"
                style={{
                  fontSize: "0.86rem",
                  fontWeight: 600,
                }}
              >
                {activity.date !== null
                  ? moment
                      .utc(activity.date)
                      .local()
                      .format("DD, MMM yyyy @HH:mm")
                  : "--"}
              </Typography>
            </StyledTableCell>
          </TableRow>
        );
      });
  };

  const fetchActivities = (page?: number, paginating: boolean = false) => {
    if (!paginating) {
      setLoadingStatus(LoadingStatus.loading);
    } else {
      setLoadingStatus(LoadingStatus.paginating);
    }

    ICIPAdminService.adminActivities(page).then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let paginator = Paginator.fromJson(
          JSON.parse(JSON.stringify(response.data.data)).data,
          DataTypeEnum.activity_schema
        );
        let wallets: Array<BusinessAccount> = JSON.parse(
          JSON.stringify(response.data.data)
        ).wallets.map((element) => {
          return BusinessAccount.fromJson(element);
        });

        setData({
          original: paginator,
          filtered: paginator,
          wallets: wallets,
        });
        setLoadingStatus(LoadingStatus.succeed);
      } else {
        setLoadingStatus(LoadingStatus.failure);
      }
    });
  };

  useEffect(() => {
    fetchActivities(null, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${intl.formatMessage({
          id: "sharedScreen.activities",
        })}`}
      />
      <Box className={classes.root}>
        <Box component="div" className={classes.header} mb={1}>
          <Box component="div" className={classes.headerWrapper}>
            <Box component="div" mr={1}>
              <Typography variant="h3" component="h3">
                <FormattedMessage id="sharedScreen.allActivities" />
              </Typography>
            </Box>
            <Box component="div" className={classes.counter}>
              {loadingStatus === LoadingStatus.succeed ||
              loadingStatus === LoadingStatus.paginating ? (
                <Typography variant="h3" component="h3">
                  {` ⸱ #${data != null ? data.filtered.total : 0}`}
                </Typography>
              ) : (
                <></>
              )}
            </Box>
          </Box>
          {loadingStatus === LoadingStatus.failure ? (
            errorWrapper()
          ) : loadingStatus === LoadingStatus.loading ? (
            <Box component="div" mt={1} className={classes.loadingWrapper}>
              <Box component="div" mr={2} mt={1}>
                <CircularProgress
                  color="secondary"
                  disableShrink={false}
                  size={20}
                  thickness={4}
                />
              </Box>
              <Typography component="span" className="caption">
                <FormattedMessage id="sharedScreen.loadingData" />
              </Typography>
            </Box>
          ) : (
            <></>
          )}
        </Box>
        {loadingStatus === LoadingStatus.succeed ||
        loadingStatus === LoadingStatus.paginating ? (
          <>
              <Box mt={4} component="div" style={{borderBottom: "1px solid #e8e8e8"}}>
                  <ExpandedContent title={intl.formatMessage({ id: "sharedScreen.balances" })} defaultExpanded={false}>
                      <Box pb={3}>
                          <BalanceDetails accounts={data.wallets} />
                      </Box>
                  </ExpandedContent>
              </Box>
            <Box component="div" className={classes.searchHeaderWrapper} pt={1}>
              <Box component="div" className={classes.paginatorWrapper}>
                <Box>
                  <CustomTooltip
                    title={intl.formatMessage({ id: "sharedScreen.refresh" })}
                    arrow
                  >
                    <IconButton
                      disabled={false}
                      className={classes.extraAction}
                      onClick={(_) => fetchActivities(null)}
                    >
                      <ReplayIcon fontSize="small" />
                    </IconButton>
                  </CustomTooltip>
                </Box>
                <Divider orientation="vertical" variant="middle" flexItem />
                <Box ml={2}>
                  <DynamicPaginationActions
                    loading={loadingStatus === LoadingStatus.paginating}
                    enabled={
                      query.length === 0 &&
                      loadingStatus !== LoadingStatus.paginating
                    }
                    pageNumber={data.filtered.page}
                    perPage={data.filtered.per_page}
                    totalPages={data.filtered.total}
                    handlePrevAction={
                      data.filtered.prev_page !== null
                        ? () => {
                            fetchActivities(data.filtered.page - 1, true);
                          }
                        : null
                    }
                    handleNextAction={
                      data.filtered.next_page !== null
                        ? () => {
                            fetchActivities(data.filtered.page + 1, true);
                          }
                        : null
                    }
                  />
                </Box>
              </Box>
              <Box component="div" className={classes.searchWrapper}>
                <Box component="div" style={{ width: "400px" }}>
                  <InputField
                    fullWidth
                    id="search"
                    name="search"
                    placeholder={intl.formatMessage({
                      id: "sharedScreen.research",
                    })}
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    endAdornment={
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    }
                  />
                </Box>
                <Box component="div" className={classes.actionItem} ml={1}>
                  <DefaultButton aria-label="filters">
                    <Box component="div" mr={1}>
                      <TuneIcon fontSize="small" color="inherit" />
                    </Box>
                    <FormattedMessage id="sharedScreen.addFilters" />
                  </DefaultButton>
                </Box>
              </Box>
            </Box>
            <Box component="div" className={classes.tableWrapper} mb={4} mt={2}>
              <TableData
                rows={renderActivitiesTableRows()}
                header={renderActivitiesTableHeader()}
              />
            </Box>
          </>
        ) : (
          <></>
        )}
      </Box>
    </>
  );
};
